import { Link, useLocation } from "react-router-dom";
import { articles } from "@/mocks/articles";

export type NavLink = { to: string; label: string };

export const mainNav: NavLink[] = [
  { to: "/", label: "首页" },
  { to: "/category/workflow", label: "数字工作" },
  { to: "/category/read", label: "阅读新知" },
  { to: "/category/life", label: "生活灵感" },
  { to: "/category/grow", label: "认知成长" },
  { to: "/rank", label: "热门榜单" },
  { to: "/about", label: "关于看料" },
];

type SiteHeaderProps = {
  dark?: boolean;
};

export default function SiteHeader({ dark = false }: SiteHeaderProps) {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setQuery("");
  }, [location.pathname]);

  const results =
    query.trim().length > 0
      ? articles.filter(
          (a) =>
            a.title.includes(query.trim()) ||
            a.excerpt.includes(query.trim()) ||
            a.tag.some((t) => t.includes(query.trim()))
        )
      : [];

  const solid = scrolled || !dark || menuOpen || searchOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/90 backdrop-blur border-b border-background-200/70 text-foreground-950"
          : "bg-transparent text-background-50"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 md:px-6 py-3 md:py-4">
        <Link to="/" className="group flex items-center gap-2.5 whitespace-nowrap">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg font-bold text-background-50 transition-colors ${
              solid ? "bg-primary-500" : "bg-background-50/20"
            }`}
          >
            看
          </span>
          <span className="leading-none">
            <span className="block font-heading text-xl font-bold tracking-tight">
              看料
            </span>
            <span
              className={`block text-[10px] tracking-widest ${
                solid ? "text-foreground-500" : "text-background-50/80"
              }`}
            >
              每日甄选好料
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {mainNav.map((item) => {
            const active =
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  solid
                    ? active
                      ? "text-primary-700 bg-primary-100/70"
                      : "text-foreground-700 hover:text-primary-700 hover:bg-primary-50"
                    : active
                    ? "text-background-50 bg-background-50/20"
                    : "text-background-50/90 hover:text-background-50 hover:bg-background-50/15"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="搜索好料"
            className={`flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors whitespace-nowrap ${
              solid
                ? "text-foreground-700 hover:bg-background-200/70"
                : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className="ri-search-line text-base" />
            <span className="hidden md:inline">搜好料</span>
          </button>
          <Link
            to="/about"
            className={`hidden sm:inline-flex h-9 items-center rounded-md px-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              solid
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "bg-background-50 text-foreground-900 hover:bg-background-100"
            }`}
          >
            了解看料
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            className={`flex h-9 w-9 items-center justify-center rounded-md lg:hidden transition-colors ${
              solid ? "text-foreground-900 hover:bg-background-200/70" : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {menuOpen && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 py-3 lg:hidden">
          <nav className="grid grid-cols-2 gap-2" aria-label="移动导航">
            {mainNav.map((item) => {
              const active =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-800 hover:bg-background-200/60"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* 搜索浮层 */}
      {searchOpen && (
        <div className="absolute inset-x-0 top-full border-b border-background-200/70 bg-background-50 px-4 pb-5 pt-4 md:px-6">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 rounded-lg border border-background-300/70 bg-background-100 px-4 py-3">
              <i className="ri-search-line text-lg text-foreground-500" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="输入关键词，如：时间管理、复盘、阅读…"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="搜索内容关键词"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="关闭搜索"
                className="text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {query.trim().length > 0 && (
              <div className="mt-3 max-h-80 overflow-y-auto kn-scroll">
                {results.length === 0 ? (
                  <p className="px-1 py-3 text-sm text-foreground-500">
                    没有找到与「{query}」相关的好料，换个关键词试试。
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {results.map((a) => (
                      <li key={a.slug}>
                        <Link
                          to={`/article/${a.slug}`}
                          className="flex items-center justify-between gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-background-200/60"
                        >
                          <span className="text-sm font-medium text-foreground-900 line-clamp-1">
                            {a.title}
                          </span>
                          <span className="shrink-0 rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                            {a.categoryName}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}